import { useState, useEffect } from 'react';
import { 
  getAuth, 
  onAuthStateChanged, 
  GoogleAuthProvider,
  signInWithPopup,
  signOut,
  setPersistence,
  inMemoryPersistence // CHANGE: Use in-memory for zero-cache
} from 'firebase/auth';
import { 
  getFirestore, 
  collection, 
  doc, 
  addDoc, 
  updateDoc,
  deleteDoc, 
  onSnapshot,
  setDoc,
  writeBatch
} from 'firebase/firestore';
import { initializeApp, getApps, getApp } from 'firebase/app';
import { firebaseConfig, APP_ID, ADMIN_EMAILS } from '../constants/config';

const app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApp();
const auth = getAuth(app);
export const db = getFirestore(app);
const appId = APP_ID;

export const useFirebase = () => {
  const [user, setUser] = useState(null);
  const [isAdminLoggedIn, setIsAdminLoggedIn] = useState(false);
  const [posts, setPosts] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [poems, setPoems] = useState([]);
  const [diary, setDiary] = useState([]);
  const [ideas, setIdeas] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loginError, setLoginError] = useState('');
  const [googleAccessToken, setGoogleAccessToken] = useState(() => {
    return typeof window !== 'undefined' ? localStorage.getItem('google_access_token') || '' : '';
  });

  useEffect(() => {
    // FORCE memory persistence so the session is wiped on any reload
    setPersistence(auth, inMemoryPersistence);

    const unsubscribe = onAuthStateChanged(auth, (u) => {
      setUser(u);
      if (u && !u.isAnonymous && u.email && ADMIN_EMAILS.includes(u.email)) {
        setIsAdminLoggedIn(true);
        localStorage.setItem('idia_admin', 'true');
      } else {
        setIsAdminLoggedIn(false);
        localStorage.removeItem('idia_admin');
      }
      setIsLoading(false);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const postsRef = collection(db, 'artifacts', appId, 'public', 'data', 'posts');
    const unsubscribePosts = onSnapshot(postsRef, (snapshot) => {
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setPosts(data.sort((a, b) => b.createdAt - a.createdAt));
    }, (err) => {
      console.error("Firestore Error:", err);
    });

    const quotesRef = collection(db, 'artifacts', appId, 'public', 'data', 'quotes');
    const unsubscribeQuotes = onSnapshot(quotesRef, (snapshot) => {
      if (!snapshot.empty) {
        const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setQuotes(data.sort((a, b) => b.createdAt - a.createdAt));
      }
    });

    const poemsRef = collection(db, 'artifacts', appId, 'public', 'data', 'poems');
    const unsubscribePoems = onSnapshot(poemsRef, (snapshot) => {
      if (!snapshot.empty) {
        const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setPoems(data.sort((a, b) => b.createdAt - a.createdAt));
      } else {
        setPoems([]);
      }
    });

    const diaryRef = collection(db, 'artifacts', appId, 'public', 'data', 'diary');
    const unsubscribeDiary = onSnapshot(diaryRef, (snapshot) => {
      if (!snapshot.empty) {
        const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setDiary(data.sort((a, b) => {
          const dateA = a.date ? new Date(a.date) : new Date(a.createdAt || 0);
          const dateB = b.date ? new Date(b.date) : new Date(b.createdAt || 0);
          return dateB - dateA || b.createdAt - a.createdAt;
        }));
      } else {
        setDiary([]);
      }
    });

    const categoriesRef = doc(db, 'artifacts', appId, 'public', 'data', 'settings', 'categories');
    const unsubscribeCategories = onSnapshot(categoriesRef, (docSnapshot) => {
      if (docSnapshot.exists()) {
        setCategories(docSnapshot.data().list || []);
      } else {
        setCategories(["Agama", "Umum", "Politik", "Falsafah, Sains & Matematik", "Wisdom"]);
      }
    });

    const ideasRef = collection(db, 'artifacts', appId, 'public', 'data', 'ideas');
    const unsubscribeIdeas = onSnapshot(ideasRef, (snapshot) => {
      if (!snapshot.empty) {
        const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setIdeas(data.sort((a, b) => (b.updatedAt || b.createdAt) - (a.updatedAt || a.createdAt)));
      } else {
        setIdeas([]);
      }
    });

    return () => { unsubscribePosts(); unsubscribeQuotes(); unsubscribePoems(); unsubscribeDiary(); unsubscribeCategories(); unsubscribeIdeas(); };
  }, [user]);

  const handleGoogleLogin = async () => {
    setLoginError('');
    const provider = new GoogleAuthProvider();
    provider.addScope('https://www.googleapis.com/auth/drive.file');
    // Force account selection screen
    provider.setCustomParameters({ prompt: 'select_account' }); 

    try {
      const result = await signInWithPopup(auth, provider);
      if (!ADMIN_EMAILS.includes(result.user.email)) {
        setLoginError(`Access Denied: ${result.user.email} is not authorized.`);
        await handleLogout(); 
      } else {
        const credential = GoogleAuthProvider.credentialFromResult(result);
        const token = credential?.accessToken || '';
        if (token) {
          localStorage.setItem('google_access_token', token);
          setGoogleAccessToken(token);
        }
      }
    } catch (error) {
      setLoginError(`Login Error: ${error.message}`);
    }
  };

  // --- REFINED DEEP LOGOUT WITH HARD RELOAD ---
  const handleLogout = async () => {
    try {
      await signOut(auth);

      if (typeof window !== 'undefined') {
        localStorage.clear();
        sessionStorage.clear();

        const dbs = await window.indexedDB.databases();
        dbs.forEach(dbInfo => {
          if (dbInfo.name.includes('firebase')) {
            window.indexedDB.deleteDatabase(dbInfo.name);
          }
        });

        // THE NUCLEAR OPTION: Hard reload the site to clear RAM
        window.location.href = window.location.origin; 
      }

      setUser(null);
      setIsAdminLoggedIn(false);
      
    } catch (error) {
      console.error("Logout Error:", error);
    }
  };

  // ... (rest of the file remains the same: requestDelete, submitQuoteLink, etc.)
  
  const requestDelete = async (id, collectionName) => {
    try {
      const docRef = doc(db, 'artifacts', appId, 'public', 'data', collectionName, id);
      await deleteDoc(docRef);
      return { success: true }; 
    } catch (error) {
      return { success: false, error: error.message }; 
    }
  };

  const submitQuote = async (quoteData) => {
    if (!user || !quoteData.text?.trim()) return;
    try {
      await addDoc(collection(db, 'artifacts', appId, 'public', 'data', 'quotes'), {
        ...quoteData,
        likes: 0,
        createdAt: Date.now()
      });
      return { success: true };
    } catch (error) {
      throw error;
    }
  };

  const updateQuote = async (quoteId, updatedData) => {
    try {
      const quoteRef = doc(db, 'artifacts', appId, 'public', 'data', 'quotes', quoteId);
      await updateDoc(quoteRef, {
        ...updatedData,
        updatedAt: Date.now()
      });
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const submitPoem = async (poemData) => {
    if (!user || !poemData.text?.trim()) return;
    try {
      await addDoc(collection(db, 'artifacts', appId, 'public', 'data', 'poems'), {
        ...poemData,
        likes: 0,
        createdAt: Date.now()
      });
      return { success: true };
    } catch (error) {
      throw error;
    }
  };

  const updatePoem = async (poemId, updatedData) => {
    try {
      const poemRef = doc(db, 'artifacts', appId, 'public', 'data', 'poems', poemId);
      await updateDoc(poemRef, {
        ...updatedData,
        updatedAt: Date.now()
      });
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  // Helper: calculate read time from HTML or plain text content
  const calcReadTime = (content) => {
    if (!content) return '1 min read';
    // Strip HTML tags to get plain text, then count words
    const text = content.replace(/<[^>]+>/g, '');
    const words = text.trim() ? text.trim().split(/\s+/).length : 0;
    const minutes = Math.max(1, Math.ceil(words / 200));
    return `${minutes} min read`;
  };

  // Helper: extract plain text excerpt from HTML content
  const extractExcerpt = (content, maxLen = 150) => {
    if (!content) return '';
    const text = content.replace(/<[^>]+>/g, '').trim();
    return text.length > maxLen ? text.substring(0, maxLen) + '...' : text;
  };

  const submitDiary = async (diaryData) => {
    if (!user || !diaryData.content?.trim()) return;
    try {
      const docRef = await addDoc(collection(db, 'artifacts', appId, 'public', 'data', 'diary'), {
        ...diaryData,
        status: diaryData.status || 'published',
        readTime: diaryData.readTime || calcReadTime(diaryData.content),
        excerpt: diaryData.excerpt || extractExcerpt(diaryData.content),
        likes: 0,
        createdAt: Date.now()
      });
      return { success: true, id: docRef.id };
    } catch (error) {
      throw error;
    }
  };

  const updateDiary = async (diaryId, updatedData) => {
    try {
      const diaryRef = doc(db, 'artifacts', appId, 'public', 'data', 'diary', diaryId);
      const updatePayload = {
        ...updatedData,
        updatedAt: Date.now()
      };
      // Recalculate readTime if content changed
      if (updatedData.content) {
        updatePayload.readTime = calcReadTime(updatedData.content);
        updatePayload.excerpt = extractExcerpt(updatedData.content);
      }
      await updateDoc(diaryRef, updatePayload);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const saveCategories = async (newCategories) => {
    try {
      const categoriesRef = doc(db, 'artifacts', appId, 'public', 'data', 'settings', 'categories');
      await setDoc(categoriesRef, { list: newCategories }, { merge: true });
      return { success: true };
    } catch (error) {
      console.error("Error saving categories:", error);
      return { success: false, error: error.message };
    }
  };

  const bulkDelete = async (ids, collectionName) => {
    try {
      const batch = writeBatch(db);
      ids.forEach(id => {
        const docRef = doc(db, 'artifacts', appId, 'public', 'data', collectionName, id);
        batch.delete(docRef);
      });
      await batch.commit();
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const savePost = async (postData, editingPostId) => {
    try {
      if (editingPostId) {
        await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'posts', editingPostId), postData);
        return { success: true, id: editingPostId };
      } else {
        postData.createdAt = Date.now();
        const docRef = await addDoc(collection(db, 'artifacts', appId, 'public', 'data', 'posts'), postData);
        return { success: true, id: docRef.id };
      }
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  const saveIdea = async (ideaData, editingIdeaId) => {
    try {
      const payload = { ...ideaData, updatedAt: Date.now() };
      if (editingIdeaId) {
        await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'ideas', editingIdeaId), payload);
        return { success: true, id: editingIdeaId };
      } else {
        payload.createdAt = Date.now();
        const docRef = await addDoc(collection(db, 'artifacts', appId, 'public', 'data', 'ideas'), payload);
        return { success: true, id: docRef.id };
      }
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  const deleteIdea = async (ideaId) => {
    return requestDelete(ideaId, 'ideas');
  };

  return {
    user,
    isAdminLoggedIn,
    googleAccessToken,
    setGoogleAccessToken,
    posts,
    setPosts,
    quotes,
    setQuotes,
    poems,
    setPoems,
    diary,
    setDiary,
    ideas,
    setIdeas,
    isLoading,
    loginError,
    handleGoogleLogin,
    handleLogout,
    requestDelete,
    submitQuote,
    updateQuote,
    submitPoem,
    updatePoem,
    submitDiary,
    updateDiary,
    saveIdea,
    deleteIdea,
    savePost,
    bulkDelete,
    categories,
    saveCategories,
    calcReadTime,
    extractExcerpt,
    db,
    appId
  };
};